import { z } from 'zod';
import { serviceIdSchema } from './plugin-requirements';

/**
 * Connection types and the hub's connection API (`docs/adr-connections-hub.md`). A type is data:
 * what the user enters, and a request that checks it. The hub runs no plugin code.
 */

const fieldKeySchema = z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/);
/** A way of connecting of a type (`api-key` by default). */
const authIdSchema = z.string().regex(/^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/);

/** One value the user enters to connect. */
export const connectionFieldSchema = z
  .object({
    key: fieldKeySchema,
    label: z.string().min(1).max(120),
    input: z.enum(['text', 'password', 'textarea']).default('text'),
    required: z.boolean().default(true),
    /** Secret values are sealed and never shown again; the others are shown with the connection. */
    secret: z.boolean().default(false),
    placeholder: z.string().max(256).optional(),
    /** The value kept when the user leaves the field empty (a host, a region). */
    default: z.string().max(1024).optional(),
    description: z.string().max(1024).optional(),
  })
  .strict();
export type ConnectionField = z.infer<typeof connectionFieldSchema>;

const templateSchema = z.string().max(2048);
const headerNameSchema = z.string().regex(/^[A-Za-z0-9!#$%&'*+.^_`|~-]{1,64}$/);

/**
 * The API a way of connecting authenticates (`docs/adr-connections-hub.md`, proxied requests):
 * the hub sends a consumer's request under `baseUrl` with these headers and query parameters,
 * `{{key}}` filled from the connection's values (`{{access_token}}` for an OAuth grant). The
 * consumer never sees them.
 */
export const connectionApiSchema = z
  .object({
    baseUrl: templateSchema,
    headers: z
      .record(headerNameSchema, templateSchema)
      .refine((headers) => Object.keys(headers).length <= 16, 'At most 16 headers.')
      .optional(),
    query: z
      .record(z.string().regex(/^[A-Za-z0-9_.-]{1,64}$/), templateSchema)
      .refine((query) => Object.keys(query).length <= 8, 'At most 8 parameters.')
      .optional(),
  })
  .strict();
export type ConnectionApi = z.infer<typeof connectionApiSchema>;
const oauthScopeSchema = z
  .string()
  .min(1)
  .max(256)
  .regex(/^[^\s,]+$/);

/**
 * An OAuth 2.0 authorization code grant, run by the hub: the user is sent to `authorizeUrl`, the
 * hub exchanges the code at `tokenUrl` and refreshes at `refreshUrl` (`tokenUrl` when absent).
 * The OAuth client (its id, secret and `clientFields`) is configured per owner on the hub;
 * `{{key}}` in the URLs is replaced by a client field (a subdomain, a server).
 *
 * A sign-in to a remote MCP server names the server instead (`mcpServer`): the hub discovers its
 * endpoints and `resource` as the MCP authorization spec describes, and its client is registered
 * dynamically, a client ID metadata document of the hub, or the user's own OAuth app.
 *
 * `fields` are values the user enters for the connection before signing in, shown with it: the
 * address of a self-hosted server, which `{{key}}` fills in `mcpServer.url` and `api`.
 */
const oauthAuthSchema = z
  .object({
    kind: z.literal('oauth2'),
    id: authIdSchema.default('oauth'),
    label: z.string().min(1).max(120).optional(),
    authorizeUrl: templateSchema.optional(),
    tokenUrl: templateSchema.optional(),
    refreshUrl: templateSchema.optional(),
    mcpServer: z
      .object({
        /** An address, or a template of `fields` (`{{url}}/mcp`). */
        url: templateSchema.refine(
          (url) => /\{\{\s*[a-zA-Z]/.test(url) || URL.canParse(url),
          'An address, or a template of the entered fields.',
        ),
        /**
         * How the server accepts clients; `registered` takes a registered OAuth app: `clientId`,
         * a public client its publisher registered, else the user's own.
         */
        client: z.enum(['dynamic', 'metadata-document', 'registered']),
        clientId: z.string().min(1).max(256).optional(),
      })
      .strict()
      .optional(),
    scopes: z.array(oauthScopeSchema).max(128).default([]),
    scopeSeparator: z.enum([' ', ',']).default(' '),
    pkce: z.boolean().default(true),
    /** How the client authenticates at the token endpoint. */
    tokenAuth: z.enum(['basic', 'post', 'none']).default('basic'),
    tokenFormat: z.enum(['form', 'json']).default('form'),
    /** The field of the token response holding the tokens, for providers wrapping them. */
    tokenEnvelope: z.string().min(1).max(64).optional(),
    authorizeParams: z
      .record(z.string().regex(/^[A-Za-z0-9_.-]{1,64}$/), z.string().max(512))
      .refine((params) => Object.keys(params).length <= 16, 'At most 16 parameters.')
      .optional(),
    /** Values the user enters for the connection, before signing in; never secret. */
    fields: z
      .array(connectionFieldSchema.refine((field) => !field.secret, 'Entered values are shown.'))
      .max(8)
      .default([]),
    /** Values of the OAuth client besides its id and secret, configured with it. */
    clientFields: z.array(connectionFieldSchema).max(8).default([]),
    /** Where to register the OAuth client. */
    clientSetupUrl: z.url().max(2048).optional(),
    /**
     * The Crowlingo service (its name in crowid) this sign-in reaches: where the host acts for it
     * with the user's Crowlingo account, connecting at its address needs no OAuth flow.
     */
    crowlingoService: z
      .string()
      .regex(/^[a-z][a-z0-9-]{0,63}$/)
      .optional(),
    api: connectionApiSchema.optional(),
  })
  .strict()
  .refine(
    (auth) =>
      auth.mcpServer === undefined
        ? auth.authorizeUrl !== undefined && auth.tokenUrl !== undefined
        : auth.authorizeUrl === undefined && auth.tokenUrl === undefined,
    'An OAuth way of connecting has its endpoints, or an MCP server that publishes them.',
  )
  .refine(
    (auth) => new Set(auth.fields.map((field) => field.key)).size === auth.fields.length,
    'Field keys must be unique.',
  );

/** A way of connecting: values the user enters, or an OAuth grant run by the hub. */
export const connectionAuthSchema = z.discriminatedUnion('kind', [
  oauthAuthSchema,
  z
    .object({
      kind: z.literal('api-key'),
      id: authIdSchema.default('api-key'),
      label: z.string().min(1).max(120).optional(),
      fields: z.array(connectionFieldSchema).min(1).max(16),
      api: connectionApiSchema.optional(),
    })
    .strict()
    .refine(
      (auth) => new Set(auth.fields.map((field) => field.key)).size === auth.fields.length,
      'Field keys must be unique.',
    ),
]);
export type ConnectionAuth = z.infer<typeof connectionAuthSchema>;
export type OAuthConnectionAuth = Extract<ConnectionAuth, { kind: 'oauth2' }>;
export type ApiKeyConnectionAuth = Extract<ConnectionAuth, { kind: 'api-key' }>;

/**
 * A request that succeeds (2xx) when the entered values work. `{{key}}` in the URL, headers and
 * bearer is replaced by the value of that field.
 */
export const connectionTestSchema = z
  .object({
    method: z.enum(['GET', 'HEAD', 'POST']).default('GET'),
    url: templateSchema,
    headers: z
      .record(headerNameSchema, templateSchema)
      .refine((headers) => Object.keys(headers).length <= 16, 'At most 16 headers.')
      .optional(),
    bearer: templateSchema.optional(),
  })
  .strict();
export type ConnectionTest = z.infer<typeof connectionTestSchema>;

/** What a plugin declares for its own service (`<plugin>/<service>`), next to the connector itself. */
export const connectionTypeDescriptorSchema = z
  .object({
    title: z.string().min(1).max(120).optional(),
    description: z.string().max(1024).optional(),
    auth: z
      .array(connectionAuthSchema)
      .min(1)
      .max(8)
      .refine(
        (auth) => new Set(auth.map((method) => method.id)).size === auth.length,
        'Auth ids must be unique.',
      ),
    test: connectionTestSchema.optional(),
  })
  .strict();

/**
 * Who declares the connection types of the core's connector catalog services (`plugin` of their
 * `ConnectionType`): the core itself, a name no plugin can have.
 */
export const CATALOG_CONNECTION_DECLARER = 'crowdex:connector-catalog';

export const connectionTypeSchema = connectionTypeDescriptorSchema.extend({
  id: serviceIdSchema,
  title: z.string().min(1).max(120),
  /** The plugin that declares the type. */
  plugin: z.string(),
});
export type ConnectionType = z.infer<typeof connectionTypeSchema>;

type ConnectorDeclaration = {
  service: string;
  title?: string | undefined;
  description?: string | undefined;
  auth?: ConnectionAuth[] | undefined;
  test?: ConnectionTest | undefined;
};

/**
 * What the connection hub reads of a plugin: the services whose connection types it
 * declares (`connectors` with `auth`) or names, and those it requires.
 */
export type ConnectionDeclarer = {
  name: string;
  enabled: boolean;
  removed?: boolean | undefined;
  /**
   * The Crowlingo application the plugin brings (its manifest's `service`, its name in crowid):
   * its own connection is also reached with the user's Crowlingo account (`crowlingoAccountAuth`).
   */
  crowlingoService?: string | undefined;
  connectors?: readonly ConnectorDeclaration[] | undefined;
  requires?:
    | {
        connections?:
          | ReadonlyArray<{ service: string; scopes?: readonly string[] | undefined }>
          | undefined;
      }
    | undefined;
};

/**
 * The way of connecting a Crowlingo application's connection gets without declaring it: the
 * user's Crowlingo account, at the address the host knows for the service (the host acts for its
 * users with Zitadel tokens, crowid/README.md); its API takes the token as a bearer. The hub never
 * runs it: the host serves these connections itself.
 */
export function crowlingoAccountAuth(service: string): OAuthConnectionAuth {
  return {
    kind: 'oauth2',
    id: 'oauth',
    label: 'Crowlingo account',
    crowlingoService: service,
    scopes: [],
    scopeSeparator: ' ',
    pkce: true,
    tokenAuth: 'basic',
    tokenFormat: 'form',
    fields: [{ key: 'url', label: 'Address', input: 'text', required: true, secret: false }],
    clientFields: [],
    api: { baseUrl: '{{url}}', headers: { Authorization: 'Bearer {{access_token}}' } },
  };
}

/** A Crowlingo application's own connection: its declared ways, and its Crowlingo account's. */
function withCrowlingoAccount(
  auth: readonly ConnectionAuth[],
  service: string | undefined,
): ConnectionAuth[] {
  if (service === undefined) return [...auth];
  if (auth.some((item) => item.id === 'oauth' || (item.kind === 'oauth2' && item.crowlingoService)))
    return [...auth];
  return [...auth, crowlingoAccountAuth(service)];
}

/**
 * The connection types the enabled plugins declare: their own services (`<plugin>/<service>`) with an
 * auth descriptor. Catalog services get theirs from the published service index. The namespace
 * gives each of these services a single declarer. An OAuth grant asks for the scopes it declares and
 * those every enabled plugin requiring the service adds (`requires.connections[].scopes`).
 */
export function pluginConnectionTypes(
  plugins: ReadonlyArray<{
    name: string;
    enabled: boolean;
    crowlingoService?: string | undefined;
    connectors?: readonly ConnectorDeclaration[] | undefined;
    requires?:
      | {
          connections?:
            | ReadonlyArray<{ service: string; scopes?: readonly string[] | undefined }>
            | undefined;
        }
      | undefined;
  }>,
): ConnectionType[] {
  const required = new Map<string, Set<string>>();
  for (const plugin of plugins) {
    if (!plugin.enabled) continue;
    for (const requirement of plugin.requires?.connections ?? []) {
      const scopes = required.get(requirement.service) ?? new Set<string>();
      for (const scope of requirement.scopes ?? []) scopes.add(scope);
      required.set(requirement.service, scopes);
    }
  }
  const withRequiredScopes = (service: string, auth: ConnectionAuth): ConnectionAuth =>
    auth.kind === 'oauth2'
      ? { ...auth, scopes: [...new Set([...auth.scopes, ...(required.get(service) ?? [])])] }
      : auth;
  const types = new Map<string, ConnectionType>();
  for (const plugin of plugins) {
    if (!plugin.enabled) continue;
    for (const connector of plugin.connectors ?? []) {
      if (connector.auth === undefined || !connector.service.startsWith(`${plugin.name}/`))
        continue;
      if (types.has(connector.service)) continue;
      types.set(connector.service, {
        id: connector.service,
        title: connector.title ?? connector.service.slice(plugin.name.length + 1),
        ...(connector.description === undefined ? {} : { description: connector.description }),
        auth: withCrowlingoAccount(connector.auth, plugin.crowlingoService).map((auth) =>
          withRequiredScopes(connector.service, auth),
        ),
        ...(connector.test === undefined ? {} : { test: connector.test }),
        plugin: plugin.name,
      });
    }
  }
  return [...types.values()].sort((left, right) => left.title.localeCompare(right.title));
}

/**
 * The part of a type a connection was authorized with: who declares it, the way of connecting
 * and where the hub sends its values. A connection keeps it, so a plugin update (or another
 * declarer) that changes it cannot redirect a stored credential; the user enters it again.
 */
export type AuthorizedConnectionType = {
  plugin: string;
  auth: {
    kind: ConnectionAuth['kind'];
    id: string;
    fields: Array<{ key: string; secret: boolean }>;
    /** Where proxied requests send the values. */
    api?: ConnectionApi | null;
    /** OAuth: where the code, the client and the refresh token are sent. */
    oauth?: {
      authorizeUrl: string | null;
      tokenUrl: string | null;
      mcpServer: string | null;
      refreshUrl: string | null;
      tokenAuth: OAuthConnectionAuth['tokenAuth'];
      clientFields: Array<{ key: string; secret: boolean }>;
    };
  };
  test: ConnectionTest | null;
};

const fieldPins = (fields: readonly ConnectionField[]) =>
  fields.map((field) => ({ key: field.key, secret: field.secret }));

export function authorizedConnectionType(
  type: ConnectionType,
  auth: ConnectionAuth,
): AuthorizedConnectionType {
  return {
    plugin: type.plugin,
    auth:
      auth.kind === 'oauth2'
        ? {
            kind: auth.kind,
            id: auth.id,
            fields: fieldPins(auth.fields),
            ...(auth.api === undefined ? {} : { api: auth.api }),
            oauth: {
              authorizeUrl: auth.authorizeUrl ?? null,
              tokenUrl: auth.tokenUrl ?? null,
              mcpServer: auth.mcpServer?.url ?? null,
              refreshUrl: auth.refreshUrl ?? null,
              tokenAuth: auth.tokenAuth,
              clientFields: fieldPins(auth.clientFields),
            },
          }
        : {
            kind: auth.kind,
            id: auth.id,
            fields: fieldPins(auth.fields),
            ...(auth.api === undefined ? {} : { api: auth.api }),
          },
    test: type.test ?? null,
  };
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value !== null && typeof value === 'object')
    return `{${Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => `${JSON.stringify(key)}:${canonical(entry)}`)
      .join(',')}}`;
  return JSON.stringify(value);
}

/**
 * Whether a connection can be used as it was authorized: `current` when an installed plugin
 * declares its type as it was, `changed` when the declaration changed since or an OAuth grant
 * lacks a scope an enabled plugin now requires (the user connects again), `missing` when no
 * enabled plugin declares it (the connection waits for one).
 */
export function connectionDeclaration(
  connection: {
    type: string;
    authId: string;
    authorizedType: AuthorizedConnectionType;
    scopes?: readonly string[] | undefined;
  },
  types: readonly ConnectionType[],
): ConnectionDeclaration {
  const type = types.find((candidate) => candidate.id === connection.type);
  if (type === undefined) return 'missing';
  const auth = type.auth.find((candidate) => candidate.id === connection.authId);
  if (auth === undefined) return 'changed';
  if (canonical(authorizedConnectionType(type, auth)) !== canonical(connection.authorizedType))
    return 'changed';
  if (auth.kind === 'oauth2') {
    const granted = new Set(connection.scopes ?? []);
    if (auth.scopes.some((scope) => !granted.has(scope))) return 'changed';
  }
  return 'current';
}
export type ConnectionDeclaration = 'current' | 'changed' | 'missing';

/** The services a plugin may use connections of: those it requires and its own connectors. */
export function pluginConnectionServices(plugin: {
  requires?: { connections?: ReadonlyArray<{ service: string }> | undefined } | undefined;
  connectors?: ReadonlyArray<{ service: string }> | undefined;
}): Set<string> {
  return new Set([
    ...(plugin.requires?.connections ?? []).map((requirement) => requirement.service),
    ...(plugin.connectors ?? []).map((connector) => connector.service),
  ]);
}

/** The keys a template names (`{{key}}`), in order. */
export function connectionTemplateKeys(template: string): string[] {
  return [...template.matchAll(/\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g)].map(
    (match) => match[1] as string,
  );
}

/** A template in `{{key}}` form: `${key}`, the form older manifests' MCP servers use, rewritten. */
export function connectionTemplate(template: string): string {
  return template.replace(/\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g, '{{$1}}');
}

/** Replaces each `{{key}}` with that value; a placeholder without a value fails. */
export function renderConnectionTemplate(
  template: string,
  values: Readonly<Record<string, string>>,
): string {
  return template.replace(/\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g, (_match, key: string) => {
    const value = values[key];
    if (value === undefined) throw new Error(`No value for {{${key}}}.`);
    return value;
  });
}

/** The values a connection signs with that must never be shown: its secret fields, or an OAuth grant's access token. */
export function connectionSecrets(
  auth: ConnectionAuth,
  values: Readonly<Record<string, string>>,
): string[] {
  return auth.kind === 'api-key'
    ? auth.fields.filter((field) => field.secret).map((field) => values[field.key] ?? '')
    : [values['access_token'] ?? ''];
}

export const connectionOwnerKindSchema = z.enum(['user', 'organization']);
export type ConnectionOwnerKind = z.infer<typeof connectionOwnerKindSchema>;

const connectionValuesSchema = z
  .record(fieldKeySchema, z.string().max(16_384))
  .refine((values) => Object.keys(values).length <= 16, 'At most 16 values.');

export const createConnectionRequestSchema = z
  .object({
    type: serviceIdSchema,
    name: z.string().trim().min(1).max(120),
    owner: connectionOwnerKindSchema.default('user'),
    /** The auth id of the type; its first way of connecting when omitted. */
    auth: authIdSchema.optional(),
    values: connectionValuesSchema,
  })
  .strict();
export type CreateConnectionRequest = z.infer<typeof createConnectionRequestSchema>;

/**
 * Starts an OAuth grant: for a new connection, or for `connection` again (a reauthorization keeps
 * its id, name and owner). Answered with the URL to send the user to.
 */
export const startConnectionOAuthRequestSchema = z
  .object({
    type: serviceIdSchema,
    name: z.string().trim().min(1).max(120).optional(),
    owner: connectionOwnerKindSchema.default('user'),
    auth: authIdSchema.optional(),
    connection: z.uuid().optional(),
    /** The way of connecting's `fields`; a reauthorization keeps the connection's when omitted. */
    values: connectionValuesSchema.optional(),
  })
  .strict();
export type StartConnectionOAuthRequest = z.infer<typeof startConnectionOAuthRequestSchema>;

/** The OAuth client an owner registered with a provider, for one type. */
export const putConnectionOAuthClientRequestSchema = z
  .object({
    type: serviceIdSchema,
    /** The OAuth way of connecting it is for; the type's first when omitted. */
    auth: authIdSchema.optional(),
    owner: connectionOwnerKindSchema.default('user'),
    clientId: z.string().trim().min(1).max(512),
    /** Left out or empty keeps the current secret. */
    clientSecret: z.string().max(4096).optional(),
    values: connectionValuesSchema.default({}),
  })
  .strict();
export type PutConnectionOAuthClientRequest = z.infer<typeof putConnectionOAuthClientRequestSchema>;

/** An owner's OAuth client for a type, as the page shows it: never its secrets. */
export type ConnectionOAuthClientSummary = {
  type: string;
  auth: string;
  owner: ConnectionOwnerKind;
  configured: boolean;
  clientId: string | null;
  hasSecret: boolean;
  /** The client values that are not secret. */
  config: Record<string, string>;
  /** The redirect URI to register with the provider: this hub's callback. */
  redirectUri: string;
};

export const updateConnectionRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    /** New values; a secret left out or empty keeps its current value. */
    values: connectionValuesSchema.optional(),
  })
  .strict();
export type UpdateConnectionRequest = z.infer<typeof updateConnectionRequestSchema>;

/** A connection as the page shows it: never its secret values. */
export type ConnectionSummary = {
  id: string;
  type: string;
  name: string;
  owner: { kind: 'user' } | { kind: 'organization'; id: string };
  authId: string;
  /** Entered values, or an OAuth grant the hub refreshes. */
  authKind: ConnectionAuth['kind'];
  /** The values that are not secret. */
  config: Record<string, string>;
  scopes: string[];
  status: 'active' | 'needs_reauthorization' | 'revoked';
  /** Whether an installed plugin still declares its type as it was authorized. */
  declaration: ConnectionDeclaration;
  isDefault: boolean;
  generation: number;
  checkedAt: string | null;
  checkError: string | null;
  createdAt: string;
  updatedAt: string;
  /** Whether this user may change or remove it (its owner, or an organization admin). */
  canEdit: boolean;
  /**
   * `crowlingo`: no grant of its own; each user using it does so with their own Crowlingo
   * account (a Crowlingo service the cloud reaches with the user's sign-in).
   */
  account?: 'crowlingo';
};

export type ConnectionTestResult = { ok: true } | { ok: false; message: string };

/**
 * One way to connect a service's accounts, as catalogs and pages show it
 * (`docs/adr-service-plugins.md`): the catalog connector's methods and the service's MCP servers.
 */
export const connectionMethodSchema = z
  .object({
    id: z.string().min(1).max(160),
    kind: z.enum(['credentials', 'oauth', 'none', 'mcp']),
    label: z.string().min(1).max(160),
    /** OAuth: an OAuth app must be registered with the provider before anyone connects. */
    appRequired: z.boolean().optional(),
    /** MCP: how its sign-in works; absent for a server taking a token instead. */
    signIn: z.enum(['none', 'dynamic', 'metadata-document', 'registered']).optional(),
    /** MCP: the server takes a token the user pastes. */
    token: z.boolean().optional(),
    /** MCP: run by the service's vendor, or by a third party (`operator`). */
    official: z.boolean().optional(),
    operator: z.string().max(253).optional(),
  })
  .strict();
export type ConnectionMethod = z.infer<typeof connectionMethodSchema>;

/**
 * Whether a method can be used: `available`; `advanced`, with the user's own OAuth app (Crowdex
 * publishes none: the user registers one with the provider); `coming-soon`, not at all yet.
 */
export const connectionMethodAvailabilitySchema = z.enum(['available', 'advanced', 'coming-soon']);
export type ConnectionMethodAvailability = z.infer<typeof connectionMethodAvailabilitySchema>;

export const connectionMethodSummarySchema = connectionMethodSchema.extend({
  availability: connectionMethodAvailabilitySchema,
});
export type ConnectionMethodSummary = z.infer<typeof connectionMethodSummarySchema>;

/**
 * A method's availability on a host. A client ID metadata document must be fetched by the
 * provider: only a host with a public URL serves one.
 */
export function connectionMethodAvailability(
  method: ConnectionMethod,
  host: { clientMetadataDocument: boolean },
): ConnectionMethodAvailability {
  if (method.kind === 'oauth') return method.appRequired ? 'advanced' : 'available';
  if (method.kind !== 'mcp' || method.token) return 'available';
  if (method.signIn === 'metadata-document')
    return host.clientMetadataDocument ? 'available' : 'advanced';
  return method.signIn === 'registered' ? 'advanced' : 'available';
}

/**
 * Whether a method can be used now: available, or OAuth with the user's own OAuth app, which the
 * connection hub runs.
 */
export function connectionMethodUsable(method: ConnectionMethodSummary): boolean {
  return (
    method.availability === 'available' ||
    (method.kind === 'oauth' && method.availability === 'advanced')
  );
}

/**
 * A service plugin can be installed when one of its methods can be used, when it needs no
 * account, or when it brings something that works without one.
 */
export function servicePluginAvailability(
  methods: readonly ConnectionMethodSummary[],
  worksWithoutAccount: boolean,
): 'available' | 'coming-soon' {
  return worksWithoutAccount || methods.length === 0 || methods.some(connectionMethodUsable)
    ? 'available'
    : 'coming-soon';
}
